import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Radio,
  FormControlLabel,
} from '@mui/material';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import './SubscriptionOnboardingDialog.scss';
import { useAuth } from 'src/hooks/AuthProvider';
import { SubscriptionTypeChoices, Subscription } from 'src/types/interfaces';
import { GlassmorphicCard } from '../GlassmorphicCard/GlassmorphicCard';
import { ActionButton } from '../ActionButton/ActionButton';
import { OrbStepper } from '../OrbStepper/OrbStepper';
import clsx from 'clsx';
import { Button } from '../Button/Button';

// UI-specific subscription representation that extends the core subscription type
interface SubscriptionOption {
  id: SubscriptionTypeChoices;
  title: string;
  price: string;
  description: string;
}

interface SubscriptionOnboardingDialogProps {
  // When provided, these props override the auto-open behavior
  open?: boolean;
  onClose?: () => void;
}

const subscriptionOptions: SubscriptionOption[] = [
  {
    id: SubscriptionTypeChoices.TRIAL,
    title: '30-Day Pro Trial',
    price: '$0.00',
    description: 'Start exploring premium features.'
  },
  {
    id: SubscriptionTypeChoices.PRO,
    title: 'Pro Subscription',
    price: '$4.99/month',
    description: 'Full access to all features.'
  },
];

const SubscriptionOnboardingDialog: React.FC<SubscriptionOnboardingDialogProps> = ({
  open: controlledOpen,
  onClose
}) => {
  const { isAuthenticated, subscriptions } = useAuth();
  const shouldOpen = useMemo(() => {
    // Only auto-open if authenticated and doesn't have an active non-FREE subscription
    return isAuthenticated && !subscriptions?.some((sub) => (sub.type !== SubscriptionTypeChoices.FREE && sub.isActive));
  }, [isAuthenticated, subscriptions]);

  // Use controlled open state if provided, otherwise use the auto-open logic
  const [isOpen, setIsOpen] = useState(controlledOpen !== undefined ? controlledOpen : shouldOpen);
  const stripe = useStripe();
  const elements = useElements();
  const [step, setStep] = useState<number>(1);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<SubscriptionTypeChoices | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If controlled externally, update when controlledOpen changes
    if (controlledOpen !== undefined) {
      setIsOpen(controlledOpen);
    } else {
      // Otherwise update based on authentication/subscription status
      setIsOpen(shouldOpen);
    }
  }, [controlledOpen, shouldOpen]);

  const handleClose = () => {
    if (onClose) {
      // If controlled externally, call the provided onClose
      onClose();
    } else {
      // Otherwise handle internally
      setIsOpen(false);
    }
    // Reset state on close
    setStep(1);
    setSelectedSubscriptionId(null);
    setError(null);
  };

  const handleSubscriptionSelect = (subscriptionId: SubscriptionTypeChoices) => {
    setSelectedSubscriptionId(subscriptionId);
    setError(null);
  };

  const handleNext = () => {
    if (selectedSubscriptionId === SubscriptionTypeChoices.TRIAL) {
      handleFreeTrial();
    } else if (selectedSubscriptionId === SubscriptionTypeChoices.PRO) {
      setStep(2);
    } else {
      setError("Please select a subscription to continue.");
    }
  };

  const handleBack = () => {
    setStep(1);
    setError(null);
  };

  const handleFreeTrial = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      console.log('Initiating Free Trial...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Free Trial Success');
      handleClose();
    } catch (err) {
      console.error('Free Trial Error:', err);
      setError('Error initiating free trial. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setError("Payment system is not ready. Please try again shortly.");
      return;
    }
    setIsProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Payment information component not found. Please refresh.');
      setIsProcessing(false);
      return;
    }

    try {
      const paymentIntentRes = await axios.post('/api/create-payment-intent', { subscription: 'pro' });
      const clientSecret = paymentIntentRes.data.clientSecret;

      if (!clientSecret) {
        throw new Error("Failed to retrieve payment authorization.");
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement }
      });

      if (result.error) {
        setError(result.error.message || 'Payment failed. Please check your card details and try again.');
      } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        console.log('Payment Succeeded:', result.paymentIntent);
        handleClose();
      } else {
        setError('Payment did not succeed. Status: ' + (result.paymentIntent?.status || 'unknown'));
      }
    } catch (err: any) {
      console.error('Payment Processing Error:', err);
      setError(err.message || 'An unexpected error occurred during payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Render Step 1: Subscription Selection
  const renderSelectSubscription = () => (
    <Box>
      <DialogContentText className="dialogContentText">
        Choose the subscription that best suits your needs.
      </DialogContentText>
      <Grid container spacing={2} justifyContent="center" className="optionsContainer">
        {subscriptionOptions.map((subscription) => (
          <Grid item key={subscription.id} xs={12} sm={6}>
            <GlassmorphicCard
              className={clsx(
                'subscriptionCard',
                { 'subscriptionCard--selected': selectedSubscriptionId === subscription.id },
                'subscriptionCard--interactive'
              )}
              onClick={() => handleSubscriptionSelect(subscription.id)}
            >
              <Box className="subscriptionCard__contentBox">
                <FormControlLabel
                  value={subscription.id}
                  control={<Radio checked={selectedSubscriptionId === subscription.id} size="small" className="subscriptionCard__radioControl" />}
                  label=""
                  className="subscriptionCard__radioLabel"
                  labelPlacement="start"
                />
                <Typography variant="subtitle1" component="div" gutterBottom className="subscriptionCard__title">
                  {subscription.title}
                </Typography>
                <Typography variant="h6" component="div" color="primary" gutterBottom className="subscriptionCard__price">
                  {subscription.price}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {subscription.description}
                </Typography>
              </Box>
            </GlassmorphicCard>
          </Grid>
        ))}
      </Grid>
      {error && <Typography className={clsx("errorMessage", "errorMessage--small")}>{error}</Typography>}
      <Box className="formActions">
        <Button onClick={handleClose} disabled={isProcessing} data-testid="sub-later-button" intent="ghost" size="small">
          Later
        </Button>
        <ActionButton
          variant="primary"
          size="small"
          onClick={handleNext}
          disabled={isProcessing || !selectedSubscriptionId}
          data-testid="sub-next-button"
        >
          {isProcessing && selectedSubscriptionId === SubscriptionTypeChoices.TRIAL ? <CircularProgress size={20} color="inherit" /> : 'Next'}
        </ActionButton>
      </Box>
    </Box>
  );

  // Render Step 2: Payment Form (only for pro subscription)
  const renderPaymentForm = () => (
    <Box component="form" onSubmit={handleProSubmit} noValidate>
      <DialogContentText className={clsx("dialogContentText", "dialogContentText--centered")}>
        Enter your payment details for the Pro Subscription.
      </DialogContentText>
      <GlassmorphicCard className="paymentContainer">
        <CardElement options={{ style: { base: { fontSize: '15px' } } }} />
      </GlassmorphicCard>
      {error && (
        <Box className="errorMessageContainer">
          <Typography className={clsx("errorMessage", "errorMessage--small")}>{error}</Typography>
        </Box>
      )}
      <Box className={clsx("formActions", "formActions--payment")}>
        <ActionButton variant="info" size="small" onClick={handleBack} disabled={isProcessing} data-testid="sub-back-button">
          Back
        </ActionButton>
        <ActionButton
          type="submit"
          variant="primary"
          size="small"
          disabled={isProcessing || !stripe || !elements}
          data-testid="sub-subscribe-button"
        >
          {isProcessing ? <CircularProgress size={20} color="inherit" /> : 'Subscribe ($4.99/month)'}
        </ActionButton>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => !isProcessing && handleClose()}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        className: 'dialogPaper',
      }}
      aria-labelledby="subscription-onboarding-title"
    >
      <DialogTitle id="subscription-onboarding-title" sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
        {step === 1 ? 'Select Your Subscription' : 'Complete Your Subscription'}
      </DialogTitle>
      <DialogContent className="dialogContent">
        {step === 1 && renderSelectSubscription()}
        {step === 2 && renderPaymentForm()}
        <OrbStepper steps={2} currentStep={step} className="dialogStepper" />
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionOnboardingDialog;